import React, { useMemo, useState, useEffect, useCallback } from "react";
import { PageHeader } from "@/components/PageHeader";
import { Receipt, RotateCcw, Car, Wrench, Sparkles, Droplet, Package, Coins, Search, X, Save, ChevronDown, AlertTriangle, Copy } from "lucide-react";
import { VEHICLES } from "@/data/vehicles";
import { api, formatApiError } from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { toast } from "sonner";

export const LISTINO_ICONS = { wrench: Wrench, sparkles: Sparkles, package: Package, coins: Coins, droplet: Droplet, car: Car, receipt: Receipt };

const fmt = (n) => n.toLocaleString("it-IT");

export default function Fatture() {
  const { user, refetch } = useAuth();
  const [listino, setListino] = useState([]);
  const [rolePercents, setRolePercents] = useState({});
  const [swaps, setSwaps] = useState([]);
  const [qty, setQty] = useState({});
  const [fullkitCount, setFullkitCount] = useState(0);
  const [fullkitVehicleValue, setFullkitVehicleValue] = useState("");
  const [selectedVehicle, setSelectedVehicle] = useState(null);
  const [vehicleSearch, setVehicleSearch] = useState("");
  const [vehicleListOpen, setVehicleListOpen] = useState(false);
  // Swap state
  const [selectedSwap, setSelectedSwap] = useState(null);
  const [swapCount, setSwapCount] = useState(0);
  const [swapOpen, setSwapOpen] = useState(false);
  // Custom amount
  const [customAmount, setCustomAmount] = useState("");
  // Turno attivo? (per bloccare la registrazione se off-shift)
  const [onShift, setOnShift] = useState(null); // null = loading, true/false

  const checkShift = useCallback(async () => {
    try {
      const { data } = await api.get("/shifts/current");
      setOnShift(!!data);
    } catch (e) {
      console.warn("Errore verifica turno:", e);
      setOnShift(false);
    }
  }, []);

  useEffect(() => { checkShift(); if (refetch) refetch(); }, [checkShift, refetch]);

  useEffect(() => {
    api.get("/listino").then(({ data }) => {
      setListino(data);
      setQty(Object.fromEntries(data.map((i) => [i.key, 0])));
    }).catch((e) => toast.error(formatApiError(e.response?.data?.detail) || "Errore caricamento listino"));
    api.get("/config/percentages").then(({ data }) => setRolePercents(data)).catch(() => {});
    api.get("/swaps").then(({ data }) => setSwaps(data.map((s) => ({ id: s.id, name: s.target_name, price: s.workshop_price })))).catch(() => {});
  }, []);

  // Polling ogni 30s per rilevare auto-chiusura del turno lato server
  useEffect(() => {
    const id = setInterval(checkShift, 30000);
    return () => clearInterval(id);
  }, [checkShift]);

  const filteredVehicles = useMemo(() => {
    const q = vehicleSearch.trim().toLowerCase();
    if (!q) return VEHICLES.slice(0, 60);
    return VEHICLES.filter((v) => v.name.toLowerCase().includes(q)).slice(0, 60);
  }, [vehicleSearch]);

  const pickVehicle = (v) => {
    setSelectedVehicle(v);
    setFullkitVehicleValue(String(v.price));
    if (!fullkitCount || parseInt(fullkitCount, 10) === 0) setFullkitCount(1);
    setVehicleListOpen(false);
    setVehicleSearch("");
  };

  const clearVehicle = () => {
    setSelectedVehicle(null);
    setFullkitVehicleValue("");
    setFullkitCount(0);
  };

  const total = useMemo(() => {
    let sum = 0;
    listino.forEach((i) => { sum += (parseInt(qty[i.key] || 0, 10) || 0) * i.price; });
    // FullKit
    const vv = parseFloat(fullkitVehicleValue || 0) || 0;
    const fkPerUnit = vv * 0.3 + 30000;
    sum += (parseInt(fullkitCount || 0, 10) || 0) * fkPerUnit;
    // Swap
    if (selectedSwap) {
      sum += (parseInt(swapCount || 0, 10) || 0) * selectedSwap.price;
    }
    // Custom amount
    sum += parseFloat(customAmount || 0) || 0;
    return sum;
  }, [listino, qty, fullkitCount, fullkitVehicleValue, selectedSwap, swapCount, customAmount]);

  const activityTotal = useMemo(() => {
    let sum = 0;
    listino.forEach((i) => {
      if (i.activity) sum += (parseInt(qty[i.key] || 0, 10) || 0) * i.activity;
      else sum += (parseInt(qty[i.key] || 0, 10) || 0) * i.price;
    });
    const fkPerUnit = (parseFloat(fullkitVehicleValue || 0) || 0) * 0.3 + 30000;
    sum += (parseInt(fullkitCount || 0, 10) || 0) * fkPerUnit;
    if (selectedSwap) {
      sum += (parseInt(swapCount || 0, 10) || 0) * selectedSwap.price;
    }
    sum += parseFloat(customAmount || 0) || 0;
    return sum;
  }, [listino, qty, fullkitCount, fullkitVehicleValue, selectedSwap, swapCount, customAmount]);

  const reset = () => {
    setQty(Object.fromEntries(listino.map((i) => [i.key, 0])));
    setFullkitCount(0);
    setFullkitVehicleValue("");
    setSelectedVehicle(null);
    setVehicleSearch("");
    setSelectedSwap(null);
    setSwapCount(0);
    setCustomAmount("");
  };

  const [saving, setSaving] = useState(false);

  const rolePercent = rolePercents[user?.role] ?? 0;
  const commission = Math.round(total * rolePercent);
  const isProprietario = user?.role === "Proprietario";
  // I Proprietari possono registrare fatture anche off-shift
  const canRegister = isProprietario || onShift === true;

  const buildItems = useCallback(() => {
    const out = [];
    listino.forEach((i) => {
      const q = parseInt(qty[i.key] || 0, 10) || 0;
      if (q > 0) out.push({ key: i.key, label: i.label, qty: q, unit_price: i.price, subtotal: q * i.price });
    });
    const fkQty = parseInt(fullkitCount || 0, 10) || 0;
    if (fkQty > 0) {
      const vv = parseFloat(fullkitVehicleValue || 0) || 0;
      const unit = Math.round(vv * 0.3 + 30000);
      out.push({
        key: "fullkit",
        label: `FullKit${selectedVehicle ? ` (${selectedVehicle.name})` : ""}`,
        qty: fkQty,
        unit_price: unit,
        subtotal: fkQty * unit,
      });
    }
    const spQty = parseInt(swapCount || 0, 10) || 0;
    if (spQty > 0 && selectedSwap) {
      out.push({
        key: "swap",
        label: `Swap ${selectedSwap.name}`,
        qty: spQty,
        unit_price: selectedSwap.price,
        subtotal: spQty * selectedSwap.price,
      });
    }
    const customVal = parseFloat(customAmount || 0) || 0;
    if (customVal > 0) {
      out.push({
        key: "custom",
        label: "Importo personalizzato",
        qty: 1,
        unit_price: customVal,
        subtotal: customVal,
      });
    }
    return out;
  }, [listino, qty, fullkitCount, fullkitVehicleValue, selectedVehicle, swapCount, selectedSwap, customAmount]);

  const receiptText = useMemo(() => String(Math.round(total)), [total]);

  const copyReceipt = async () => {
    if (total <= 0) {
      toast.error("Inserisci almeno una voce per generare la fattura");
      return;
    }
    try {
      await navigator.clipboard.writeText(receiptText);
    } catch {
      const ta = document.createElement("textarea");
      ta.value = receiptText;
      document.body.appendChild(ta);
      ta.select();
      document.execCommand("copy");
      document.body.removeChild(ta);
    }
    toast.success("Totale fattura copiato negli appunti");
  };

  const saveFattura = async () => {
    if (!canRegister) {
      toast.error("Devi essere in servizio per registrare una fattura. Vai in Turno di Servizio e avvia il turno.");
      return;
    }
    if (total <= 0) {
      toast.error("Inserisci almeno una voce prima di registrare");
      return;
    }
    const items = buildItems();
    setSaving(true);
    try {
      await api.post("/fatture", {
        items,
        vehicle_name: selectedVehicle?.name || null,
        total,
        activity_total: activityTotal,
      });
      toast.success(`Fattura registrata · Stipendio netto $${fmt(commission)}`);
      reset();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail));
      // Se il server ha rifiutato per turno mancante/scaduto, aggiorna lo stato locale
      if (e.response?.status === 403) checkShift();
    } finally { setSaving(false); }
  };

  return (
    <div className="w-full">
      <PageHeader
        icon={Receipt}
        title="Fatture"
        subtitle="Calcolo rapido e registrazione fatture."
        right={
          <div className="ot-label flex items-center gap-2">
            <span className="w-2 h-2 bg-emerald-400 rounded-full" />
            VALUTA · DOLLARI ($)
          </div>
        }
      />

      {onShift === false && !isProprietario && (
        <div className="ot-panel p-4 mb-6 border-amber-500/40 bg-amber-500/[0.05] flex items-center gap-3" data-testid="off-shift-banner">
          <AlertTriangle className="w-5 h-5 text-amber-400 shrink-0" />
          <div className="text-sm">
            <div className="text-amber-400 font-bold">Non sei in servizio</div>
            <div className="text-slate-400 text-xs mt-0.5">
              Per registrare una fattura devi prima avviare il turno da <b>Turno di Servizio</b>. Se non registri fatture per 1 ora, il turno viene chiuso automaticamente.
            </div>
          </div>
        </div>
      )}

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Left: listino */}
        <div className="ot-panel-elev p-4 ot-fade-up">
          <h2 className="ot-heading text-lg font-bold text-slate-100 mb-2">Listino Prezzi</h2>
          <div className="space-y-0.5">
            {listino.map((i) => {
              const Icon = LISTINO_ICONS[i.icon] || Wrench;
              return (
                <div key={i.id || i.key} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5">
                  <div className="flex items-center gap-3">
                    <Icon className="w-4 h-4 text-blue-400/70" />
                    <span className="text-slate-200 text-sm">{i.label}</span>
                  </div>
                  <div className="ot-mono font-bold text-blue-400">
                    ${fmt(i.price)}
                  </div>
                </div>
              );
            })}
            {/* Swap sub-list */}
            {swaps.map((sp) => (
              <div key={sp.id} className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5">
                <div className="flex items-center gap-3">
                  <Car className="w-4 h-4 text-blue-400/70" />
                  <span className="text-slate-200 text-sm">Swap {sp.name}</span>
                </div>
                <div className="ot-mono font-bold text-blue-400">${fmt(sp.price)}</div>
              </div>
            ))}
            {/* FullKit */}
            <div className="flex items-center justify-between gap-3 border-b border-white/5 py-1.5">
              <div className="flex items-center gap-3">
                <Car className="w-4 h-4 text-blue-400/70" />
                <span className="text-slate-200 text-sm">FullKit Solo Prestazionale</span>
              </div>
              <div className="ot-mono font-bold text-amber-400 text-xs">30% veicolo + $30.000</div>
            </div>
          </div>
        </div>

        {/* Right: calculator */}
        <div className="ot-panel-elev p-4 ot-fade-up" style={{ animationDelay: "80ms" }}>
          <h2 className="ot-heading text-lg font-bold text-slate-100 mb-2">Calcolo Totale</h2>

          <div className="grid grid-cols-1 xl:grid-cols-2 gap-3">
            <div className="grid grid-cols-2 gap-2 content-start">
              {listino.map((i) => (
                <div key={i.id || i.key} className="flex flex-col">
                  <label className="ot-label !text-[0.6rem] block mb-1 leading-tight flex-1" title={i.label}>{i.label} (${fmt(i.price)})</label>
                  <input
                    data-testid={`qty-${i.key}`}
                    type="number"
                    min="0"
                    className="ot-input ot-mono !py-1.5 !text-sm"
                    value={qty[i.key]}
                    onChange={(e) => setQty({ ...qty, [i.key]: e.target.value })}
                  />
                </div>
              ))}
            </div>

            <div className="space-y-2">
            <div className="ot-panel p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="ot-label">SWAP</div>
                {selectedSwap && (
                  <button onClick={() => { setSelectedSwap(null); setSwapCount(0); }} className="text-[0.65rem] text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="clear-swap">
                    <X className="w-3 h-3" /> RIMUOVI
                  </button>
                )}
              </div>

              <div className="relative">
                <label className="text-[0.7rem] text-slate-400 block mb-1">Seleziona swap</label>
                {selectedSwap ? (
                  <div className="ot-input flex items-center justify-between" data-testid="selected-swap">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-blue-400" />
                      <span className="text-slate-100 text-sm">{selectedSwap.name}</span>
                    </div>
                    <span className="ot-mono text-blue-400 text-sm">${fmt(selectedSwap.price)}</span>
                  </div>
                ) : (
                  <>
                    <button
                      type="button"
                      onClick={() => setSwapOpen(!swapOpen)}
                      className="ot-input flex items-center justify-between text-left"
                      data-testid="swap-toggle"
                    >
                      <span className="text-slate-400 text-sm">Scegli tipo di swap...</span>
                      <ChevronDown className={`w-4 h-4 text-slate-500 transition-transform ${swapOpen ? "rotate-180" : ""}`} />
                    </button>
                    {swapOpen && (
                      <div className="mt-2 ot-panel max-h-48 overflow-y-auto" data-testid="swap-list">
                        {swaps.map((sp) => (
                          <button
                            key={sp.id}
                            type="button"
                            onClick={() => { setSelectedSwap(sp); setSwapCount(1); setSwapOpen(false); }}
                            className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-blue-400/5 border-b border-white/5 last:border-0 transition-colors"
                            data-testid={`swap-opt-${sp.id}`}
                          >
                            <span className="text-slate-200 text-sm">{sp.name}</span>
                            <span className="ot-mono text-xs text-blue-400">${fmt(sp.price)}</span>
                          </button>
                        ))}
                      </div>
                    )}
                  </>
                )}
              </div>

              {selectedSwap && (
                <>
                  <div>
                    <label className="text-[0.7rem] text-slate-400 block mb-1">Quantità</label>
                    <input
                      data-testid="swap-count"
                      type="number"
                      min="0"
                      className="ot-input ot-mono"
                      value={swapCount}
                      onChange={(e) => setSwapCount(e.target.value)}
                    />
                  </div>
                  <div className="ot-mono text-xs text-blue-400 text-right">
                    Subtotale Swap: ${fmt((parseInt(swapCount || 0, 10) || 0) * selectedSwap.price)}
                  </div>
                </>
              )}
            </div>

            <div className="ot-panel p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="ot-label">FULLKIT (30% VEICOLO + $30.000)</div>
                {selectedVehicle && (
                  <button onClick={clearVehicle} className="text-[0.65rem] text-red-400 hover:text-red-300 flex items-center gap-1" data-testid="clear-vehicle">
                    <X className="w-3 h-3" /> RIMUOVI
                  </button>
                )}
              </div>

              {/* Vehicle picker */}
              <div className="relative">
                <label className="text-[0.7rem] text-slate-400 block mb-1">Seleziona veicolo</label>
                {selectedVehicle ? (
                  <div className="ot-input flex items-center justify-between" data-testid="selected-vehicle">
                    <div className="flex items-center gap-2">
                      <Car className="w-4 h-4 text-blue-400" />
                      <span className="text-slate-100 text-sm">{selectedVehicle.name}</span>
                    </div>
                    <span className="ot-mono text-blue-400 text-sm">${fmt(selectedVehicle.price)}</span>
                  </div>
                ) : (
                  <>
                    <div className="relative">
                      <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-500" />
                      <input
                        data-testid="vehicle-search"
                        type="text"
                        placeholder="Cerca veicolo (es. Adder, Zentorno...)"
                        className="ot-input !pl-9"
                        value={vehicleSearch}
                        onChange={(e) => { setVehicleSearch(e.target.value); setVehicleListOpen(true); }}
                        onFocus={() => setVehicleListOpen(true)}
                      />
                    </div>
                    {vehicleListOpen && (
                      <div className="mt-2 ot-panel max-h-48 overflow-y-auto" data-testid="vehicle-list">
                        {filteredVehicles.length === 0 ? (
                          <div className="p-3 text-center text-slate-500 text-xs">Nessun veicolo trovato</div>
                        ) : (
                          filteredVehicles.map((v) => (
                            <button
                              key={v.name}
                              type="button"
                              onClick={() => pickVehicle(v)}
                              className="w-full flex items-center justify-between px-3 py-2 text-left hover:bg-blue-400/5 border-b border-white/5 last:border-0 transition-colors"
                              data-testid={`vehicle-opt-${v.name.toLowerCase().replace(/[^a-z0-9]/g, '-')}`}
                            >
                              <span className="text-slate-200 text-sm">{v.name}</span>
                              <span className="ot-mono text-xs text-blue-400">${fmt(v.price)}</span>
                            </button>
                          ))
                        )}
                      </div>
                    )}
                  </>
                )}
              </div>

              <div className="ot-mono text-xs text-blue-400 text-right">
                Prezzo unitario FullKit: ${fmt(Math.round(((parseFloat(fullkitVehicleValue || 0) || 0) * 0.3 + 30000)))}
              </div>
            </div>

            <div className="ot-panel p-4 space-y-3">
              <div className="flex items-center justify-between">
                <div className="ot-label">IMPORTO PERSONALIZZATO</div>
                {(customAmount) && (
                  <button
                    onClick={() => { setCustomAmount(""); }}
                    className="text-[0.65rem] text-red-400 hover:text-red-300 flex items-center gap-1"
                    data-testid="clear-custom"
                  >
                    <X className="w-3 h-3" /> RIMUOVI
                  </button>
                )}
              </div>
              <div>
                <label className="text-[0.7rem] text-slate-400 block mb-1">Importo ($)</label>
                <input
                  data-testid="custom-amount"
                  type="number"
                  className="ot-input ot-mono"
                  placeholder="0"
                  value={customAmount}
                  onChange={(e) => setCustomAmount(e.target.value)}
                />
              </div>
              {(parseFloat(customAmount || 0) || 0) > 0 && (
                <div className="ot-mono text-xs text-blue-400 text-right">
                  Subtotale personalizzato: ${fmt(Math.round(parseFloat(customAmount) || 0))}
                </div>
              )}
            </div>
            </div>
          </div>

          {/* Totals */}
          <div className="mt-3 space-y-2">
            <div className="ot-panel p-3 relative overflow-hidden">
              <div className="ot-label mb-1 text-center">TOTALE FATTURA</div>
              <div className="ot-mono text-3xl font-black text-center text-blue-400" style={{ textShadow: "0 0 24px rgba(0,229,255,0.4)" }} data-testid="fattura-total">
                $ {fmt(Math.round(total))}
              </div>
            </div>

            {/* Commission preview */}
            <div className="ot-panel-elev p-3">
              <div className="flex items-center justify-between">
                <div>
                  <div className="ot-label">IL TUO STIPENDIO NETTO</div>
                  <div className="ot-mono text-[0.65rem] text-slate-500 tracking-widest mt-0.5">
                    {user?.role} · {Math.round(rolePercent * 100)}%
                  </div>
                </div>
                <div className="ot-mono text-2xl font-black text-amber-400" data-testid="commission-preview">
                  $ {fmt(commission)}
                </div>
              </div>
            </div>
          </div>

          <div className="grid grid-cols-3 gap-2 mt-2">
            <button
              data-testid="reset-btn"
              onClick={reset}
              className="ot-btn-ghost flex items-center justify-center gap-2"
            >
              <RotateCcw className="w-3.5 h-3.5" />
              Resetta
            </button>
            <button
              data-testid="copy-receipt-btn"
              onClick={copyReceipt}
              disabled={total <= 0}
              className="ot-btn-ghost flex items-center justify-center gap-2"
              title={total > 0 ? `Copia "${receiptText}"` : ""}
            >
              <Copy className="w-3.5 h-3.5" />
              Copia Fattura
            </button>
            <button
              data-testid="save-fattura-btn"
              onClick={saveFattura}
              disabled={saving || total <= 0 || !canRegister}
              className="ot-btn-primary flex items-center justify-center gap-2"
              title={!canRegister ? "Devi essere in servizio per registrare" : ""}
            >
              <Save className="w-4 h-4" />
              {saving ? "Registro..." : !canRegister ? "Fuori Servizio" : "Registra"}
            </button>
          </div>
          {total > 0 && (
            <div className="ot-mono text-[0.7rem] text-slate-500 mt-1.5 text-right">
              Testo copiato: <span className="text-slate-300" data-testid="receipt-preview">{receiptText}</span>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
